/**
 * L1J Web Client - StorageManager
 * localStorage 세션 유지 관리
 *
 * 브라우저 새로 고침 시 로그인 세션을 유지하기 위해 다음을 저장한다:
 *  - l1j_credentials : { account, state, charName, savedAt }
 *    (성공한 로그인 정보를 보관 → 새로 고침 후 자동 로그인에 사용)
 *
 * 보안: 비밀번호는 localStorage에 **저장하지 않는다** (XSS 등으로 평문 유출 위험).
 * StoredCredentials.password 필드는 API 호환성을 위해 남아 있지만 항상 빈 문자열이다.
 * 과거 버전이 저장한 평문 비밀번호는 로드 시점에 마이그레이션으로 제거된다.
 */

import { EventEmitter, gameEvents } from './EventEmitter';
import { StorageData, SessionState } from './types';

/** 자동 로그인 자격증명 저장 형태 (브라우저 새로 고침 후 복원에 사용)
 *  password는 보안상 저장하지 않으므로 항상 빈 문자열이다. */
export interface StoredCredentials {
  account: string;
  password: string;
  state: SessionState;
  charName: string | null;
  savedAt: number;
}

/**
 * 퀵슬롯(F5~F12) 등록 한 칸의 저장 형태.
 *
 * objId(`id`)만 남기면 아이템을 다 쓰거나 다시 주웠을 때 새 objId가 붙어 등록이
 * 영영 풀린다. 그래서 재바인딩 근거로 `gfxId`와 표시명을 함께 저장한다
 * (GameScene의 `syncQuickSlots()`가 같은 종류를 찾아 objId를 갈아 끼운다).
 */
export interface StoredQuickSlot {
  id: number;
  gfxId: number;
  name: string;
}

/** 기본 credential 만료 시간 (24시간) */
const DEFAULT_CREDENTIAL_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * StorageManager 클래스
 * localStorage를 사용하여 세션 및 사용자 설정 유지
 */
export class StorageManager {
  private static instance: StorageManager;
  private events: EventEmitter;

  // localStorage 키
  // SESSION_KEY(l1j_session)는 더 이상 저장하지 않는 레거시 키 —
  // load()의 하위 호환 읽기와 clearAll()의 잔여 데이터 정리에만 사용된다.
  private static readonly SESSION_KEY = 'l1j_session';
  private static readonly PREFERENCES_KEY = 'l1j_preferences';
  private static readonly LAST_CHAR_KEY = 'l1j_last_char';
  private static readonly CREDENTIALS_KEY = 'l1j_credentials';
  private static readonly QUICKSLOTS_KEY = 'l1j_quickslots';

  /** 자동 로그인 자격증명 만료 시간 (밀리초) — 기본 24시간 */
  private credentialTtlMs: number = DEFAULT_CREDENTIAL_TTL_MS;

  // 기본값
  private defaultSession = {
    session: {
      account: null as string | null,
      lastLogin: null as number | null,
    },
    preferences: {
      audioEnabled: true,
      musicVolume: 0.5,
      sfxVolume: 0.7,
    },
  };

  /**
   * 생성자
   */
  private constructor() {
    this.events = new EventEmitter();
  }

  /**
   * Singleton 인스턴스 반환
   */
  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  // ==================== 마지막 캐릭터 ====================

  /**
   * 마지막으로 플레이한 캐릭터 저장
   * @param charName 캐릭터명
   */
  saveLastCharacter(charName: string): void {
    try {
      localStorage.setItem(StorageManager.LAST_CHAR_KEY, charName);
      this.events.emit('lastChar:saved', { charName });
    } catch (error) {
      console.error('[StorageManager] Failed to save last character:', error);
    }
  }

  /**
   * 마지막으로 플레이한 캐릭터 로드
   * @returns 캐릭터명 또는 null
   */
  loadLastCharacter(): string | null {
    try {
      return localStorage.getItem(StorageManager.LAST_CHAR_KEY);
    } catch (error) {
      console.error('[StorageManager] Failed to load last character:', error);
      return null;
    }
  }

  // ==================== 환경설정 저장 ====================

  /**
   * 환경설정 저장
   * @param preferences 환경설정 객체
   */
  savePreferences(preferences: Partial<StorageData['preferences']>): void {
    try {
      const data = this.load();
      data.preferences = { ...data.preferences, ...preferences };
      localStorage.setItem(StorageManager.PREFERENCES_KEY, JSON.stringify(data.preferences));
      this.events.emit('preferences:saved', data.preferences);
      gameEvents.emit('storage:preferencesSaved', data.preferences);
    } catch (error) {
      console.error('[StorageManager] Failed to save preferences:', error);
    }
  }

  /**
   * 환경설정 로드
   * @returns 환경설정 객체
   */
  loadPreferences(): StorageData['preferences'] {
    try {
      const raw = localStorage.getItem(StorageManager.PREFERENCES_KEY);
      if (!raw) return { ...this.defaultSession.preferences };

      const data = JSON.parse(raw);
      return {
        audioEnabled: data.audioEnabled ?? this.defaultSession.preferences.audioEnabled,
        musicVolume: data.musicVolume ?? this.defaultSession.preferences.musicVolume,
        sfxVolume: data.sfxVolume ?? this.defaultSession.preferences.sfxVolume,
      };
    } catch (error) {
      console.error('[StorageManager] Failed to load preferences:', error);
      return { ...this.defaultSession.preferences };
    }
  }

  // ==================== 퀵슬롯 (F5~F12) ====================

  /**
   * 퀵슬롯 등록을 캐릭터별로 저장한다.
   *
   * 서버는 단축키 등록을 보관하지 않으므로(원본 클라이언트도 로컬 파일에 둔다)
   * 브라우저 localStorage가 유일한 보관처다. 계정이 아니라 **캐릭터** 단위인 이유는
   * 한 계정의 캐릭터마다 쓰는 아이템이 다르기 때문이다.
   *
   * @param charName 캐릭터명. 비어 있으면 저장하지 않는다 (덮어쓸 대상이 불분명).
   * @param slots 슬롯 순서 그대로의 배열. 빈 칸은 null.
   */
  saveQuickSlots(charName: string, slots: (StoredQuickSlot | null)[]): void {
    if (!charName) return;
    try {
      const all = this.loadAllQuickSlots();
      all[charName] = slots;
      localStorage.setItem(StorageManager.QUICKSLOTS_KEY, JSON.stringify(all));
      this.events.emit('quickSlots:saved', { charName });
    } catch (error) {
      console.error('[StorageManager] Failed to save quick slots:', error);
    }
  }

  /**
   * 저장된 퀵슬롯 등록을 읽는다. 없으면 빈 배열.
   * 손상된 항목은 조용히 null로 떨어뜨린다 — 등록 하나 때문에 전부 잃지 않게.
   */
  loadQuickSlots(charName: string): (StoredQuickSlot | null)[] {
    if (!charName) return [];
    try {
      const saved = this.loadAllQuickSlots()[charName];
      if (!Array.isArray(saved)) return [];
      return saved.map((entry) => {
        if (!entry || typeof entry.id !== 'number') return null;
        return {
          id: entry.id,
          gfxId: typeof entry.gfxId === 'number' ? entry.gfxId : 0,
          name: typeof entry.name === 'string' ? entry.name : '',
        };
      });
    } catch (error) {
      console.error('[StorageManager] Failed to load quick slots:', error);
      return [];
    }
  }

  private loadAllQuickSlots(): Record<string, (StoredQuickSlot | null)[]> {
    const raw = localStorage.getItem(StorageManager.QUICKSLOTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  }

  // ==================== 전체 데이터 ====================

  /**
   * 전체 데이터 로드
   * @returns 저장된 모든 데이터
   */
  load(): StorageData {
    try {
      const sessionRaw = localStorage.getItem(StorageManager.SESSION_KEY);
      const prefsRaw = localStorage.getItem(StorageManager.PREFERENCES_KEY);

      const session = sessionRaw ? JSON.parse(sessionRaw) : { ...this.defaultSession.session };
      const preferences = prefsRaw ? JSON.parse(prefsRaw) : { ...this.defaultSession.preferences };

      return { session, preferences };
    } catch (error) {
      console.error('[StorageManager] Failed to load data:', error);
      // 정상 경로처럼 깊은 복사로 반환 — 호출부가 반환 객체를 수정해도
      // defaultSession 원본이 오염되지 않도록 한다.
      return {
        session: { ...this.defaultSession.session },
        preferences: { ...this.defaultSession.preferences },
      };
    }
  }

  /**
   * 모든 데이터 삭제
   */
  clearAll(): void {
    try {
      localStorage.removeItem(StorageManager.SESSION_KEY);
      localStorage.removeItem(StorageManager.PREFERENCES_KEY);
      localStorage.removeItem(StorageManager.LAST_CHAR_KEY);
      localStorage.removeItem(StorageManager.CREDENTIALS_KEY);
      localStorage.removeItem(StorageManager.QUICKSLOTS_KEY);
      this.events.emit('all:cleared', {});
      gameEvents.emit('storage:allCleared', {});
    } catch (error) {
      console.error('[StorageManager] Failed to clear all:', error);
    }
  }

  /**
   * 다음 한 번의 자동 로그인 시도를 건너뛰게 한다 (탭 간 메모리 플래그, localStorage에는
   * 저장하지 않음). 다른 곳에서 같은 계정으로 로그인해 이 탭이 강제 로그아웃된 직후
   * LoginScene으로 돌아가면 곧바로 자동 로그인을 시도해 방금 접속한 세션을 도로
   * 끊어버리는 핑퐁을 막기 위한 용도.
   */
  private suppressAutoLoginOnce = false;

  suppressNextAutoLogin(): void {
    this.suppressAutoLoginOnce = true;
  }

  /** 억제 플래그를 소비(1회성)하고 그 이전 상태를 반환한다. */
  consumeAutoLoginSuppression(): boolean {
    const wasSet = this.suppressAutoLoginOnce;
    this.suppressAutoLoginOnce = false;
    return wasSet;
  }

  // ==================== 자동 로그인 자격증명 ====================

  /**
   * 로그인 성공 시 자격증명을 저장한다.
   * 브라우저 새로 고침 후 `loadCredentials()` 로 복원하여 자동 로그인에 사용한다.
   *
   * 보안: 두 번째 인자로 받은 비밀번호는 **저장하지 않는다** (localStorage 평문
   * 유출 방지). 시그니처는 호환성을 위해 유지한다.
   * @param account 계정명
   * @param _password 비밀번호 (무시됨 — 저장하지 않음)
   * @param state 로그인 직후 세션 상태
   * @param charName 마지막 선택 캐릭터 (선택)
   */
  saveCredentials(
    account: string,
    _password: string,
    state: SessionState = SessionState.AUTHENTICATED,
    charName: string | null = null,
  ): void {
    try {
      const data: StoredCredentials = {
        account,
        password: '', // 비밀번호는 저장하지 않는다
        state,
        charName,
        savedAt: Date.now(),
      };
      localStorage.setItem(StorageManager.CREDENTIALS_KEY, JSON.stringify(data));
      this.events.emit('credentials:saved', { account });
      gameEvents.emit('storage:credentialsSaved', { account });
    } catch (error) {
      console.error('[StorageManager] Failed to save credentials:', error);
    }
  }

  /**
   * 저장된 자격증명을 로드한다.
   * 만료된 자격증명은 null 로 반환한다.
   * password는 항상 빈 문자열이다 (저장하지 않으므로). 과거 버전이 남긴
   * 평문 비밀번호가 발견되면 즉시 제거(마이그레이션 재저장)한다.
   */
  loadCredentials(): StoredCredentials | null {
    try {
      const raw = localStorage.getItem(StorageManager.CREDENTIALS_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw) as Partial<StoredCredentials>;
      if (!data.account || !data.savedAt) {
        return null;
      }

      if (Date.now() - data.savedAt > this.credentialTtlMs) {
        // 만료된 자격증명 — 정리 후 null 반환
        this.clearCredentials();
        return null;
      }

      // 마이그레이션: 과거 버전이 저장한 평문 비밀번호가 남아 있으면 제거 후 재저장
      if (data.password) {
        const migrated = { ...data, password: '' };
        localStorage.setItem(StorageManager.CREDENTIALS_KEY, JSON.stringify(migrated));
      }

      return {
        account: data.account,
        password: '',
        state: (data.state as SessionState) ?? SessionState.AUTHENTICATED,
        charName: data.charName ?? null,
        savedAt: data.savedAt,
      };
    } catch (error) {
      console.error('[StorageManager] Failed to load credentials:', error);
      return null;
    }
  }

  /**
   * 저장된 자격증명이 있고 아직 유효한지 확인한다.
   * (브라우저 새로 고침 후 자동 로그인 가능 여부 판단용)
   */
  hasValidCredentials(): boolean {
    return this.loadCredentials() !== null;
  }

  /**
   * 자격증명 삭제 (로그아웃 / 만료 시)
   */
  clearCredentials(): void {
    try {
      localStorage.removeItem(StorageManager.CREDENTIALS_KEY);
      this.events.emit('credentials:cleared', {});
      gameEvents.emit('storage:credentialsCleared', {});
    } catch (error) {
      console.error('[StorageManager] Failed to clear credentials:', error);
    }
  }

  /**
   * 자격증명 만료 시간 변경 (밀리초)
   */
  setCredentialTtl(ttlMs: number): void {
    this.credentialTtlMs = Math.max(0, ttlMs);
  }

  // ==================== 이벤트 ====================

  /**
   * 환경설정 저장 이벤트 리스너
   */
  onPreferencesSaved(callback: (prefs: StorageData['preferences']) => void): void {
    this.events.on('preferences:saved', callback);
  }

  /**
   * 전체 삭제 이벤트 리스너
   */
  onAllCleared(callback: () => void): void {
    this.events.on('all:cleared', callback);
  }

  // ==================== 디버그 ====================

  /**
   * 디버그용 스토리지 정보 출력
   */
  debug(): void {
    const data = this.load();
    const creds = this.loadCredentials();
    console.log('[StorageManager]', {
      session: data.session,
      preferences: data.preferences,
      hasCredentials: !!creds,
      credentialsAccount: creds?.account ?? null,
      credentialsSavedAt: creds ? new Date(creds.savedAt).toLocaleString() : null,
    });
  }
}

/**
 * 기본 인스턴스 내보내기
 */
export const storageManager = StorageManager.getInstance();

/**
 * 기본 내보내기
 */
export default storageManager;
